import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import FunnelBars from "../components/FunnelBars";
import {
  Flame, CalendarCheck, ExternalLink, X, Mail, Eye, MousePointerClick, Clock,
} from "lucide-react";

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "report", label: "Hanno aperto il report" },
  { key: "hot", label: "Lead caldi" },
  { key: "booked", label: "Call prenotate" },
];

const STAGE_COLOR = (i) => (
  i >= 6 ? {bg:"#dcfce7",color:"#15803d"} :
  i >= 5 ? {bg:"#dbeafe",color:"#1d4ed8"} :
  i >= 4 ? {bg:"#fef3c7",color:"#92400e"} :
  i >= 3 ? {bg:"#ffedd5",color:"#9a3412"} :
  i >= 2 ? {bg:"#f1f5f9",color:"#475569"} :
           {bg:"#f8fafc",color:"#94a3b8"}
);

const dt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", {day:"2-digit",month:"2-digit",
  year:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—";

const Dot = ({ on, title }) => (
  <span title={title} style={{display:"inline-block",width:10,height:10,borderRadius:99,
    background: on ? "var(--mv-success)" : "#e2e8f0"}}/>
);

const Timeline = ({ lead, onClose, onBooked }) => {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api.get(`/funnel/leads/${lead.analysisId}/timeline`).then(r => setEvents(r.data.events));
  }, [lead.analysisId]);
  const icon = { email: Mail, report: Eye, booking: CalendarCheck };
  return (
    <div onClick={onClose} data-testid="funnel-timeline-overlay"
      style={{position:"fixed",inset:0,background:"rgba(15,23,42,.45)",zIndex:60,
              display:"flex",justifyContent:"flex-end"}}>
      <div onClick={(e)=>e.stopPropagation()} data-testid="funnel-timeline-panel"
        style={{background:"#fff",width:"min(460px,100%)",height:"100%",overflowY:"auto",padding:22}}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="h-section">{lead.companyName}</div>
            <div className="text-small text-muted mt-1">{lead.website || lead.email || "—"}</div>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="funnel-timeline-close">
            <X size={16}/>
          </button>
        </div>

        <div className="flex gap-2 mb-5" style={{flexWrap:"wrap"}}>
          {lead.publicToken && (
            <a href={`/analisi/${lead.publicToken}`} target="_blank" rel="noreferrer"
              className="btn btn-sm btn-secondary" data-testid="funnel-open-report">
              Apri report <ExternalLink size={13}/>
            </a>
          )}
          <button onClick={() => onBooked(lead, !lead.callBookedAt)}
            className={`btn btn-sm ${lead.callBookedAt ? "btn-secondary" : "btn-primary"}`}
            data-testid="funnel-toggle-booked">
            <CalendarCheck size={14}/> {lead.callBookedAt ? "Annulla call prenotata" : "Segna call prenotata"}
          </button>
        </div>

        {events === null ? <span className="spinner"/> : events.length === 0 ? (
          <div className="text-muted text-small">Nessuna attività registrata.</div>
        ) : (
          <div style={{display:"grid",gap:14}}>
            {events.map((e, i) => {
              const Icon = icon[e.kind] || Clock;
              return (
                <div key={i} className="flex gap-3" data-testid={`timeline-event-${i}`}>
                  <div style={{width:30,height:30,borderRadius:99,background:"#f8fafc",
                       display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <Icon size={15} color="var(--mv-primary)"/>
                  </div>
                  <div>
                    <div style={{fontWeight:600,fontSize:13.5}}>{e.label}</div>
                    <div className="text-small text-muted">
                      {dt(e.at)}{e.dwellSeconds ? ` · ${e.dwellSeconds}s sul report` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Funnel() {
  const [params, setParams] = useSearchParams();
  const [days, setDays] = useState(90);
  const [only, setOnly] = useState(params.get("only") || "all");
  const [listId, setListId] = useState("");
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState(null);
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const q = `days=${days}${listId ? `&listId=${listId}` : ""}`;
    const [s, l] = await Promise.all([
      api.get(`/funnel/summary?${q}`),
      api.get(`/funnel/leads?${q}&only=${only}`),
    ]);
    setSummary(s.data);
    setLeads(l.data.items);
    if (l.data.lists.length) setLists(l.data.lists);
  }, [days, listId, only]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setParams(only === "all" ? {} : { only }); }, [only, setParams]);

  const markBooked = async (lead, booked) => {
    await api.post(`/funnel/leads/${lead.analysisId}/booked`, { booked });
    setOpen(o => o && o.analysisId === lead.analysisId
      ? { ...o, callBookedAt: booked ? new Date().toISOString() : null } : o);
    load();
  };

  return (
    <div data-testid="funnel-page">
      <div className="flex justify-between items-center mb-6" style={{flexWrap:"wrap",gap:12}}>
        <div>
          <div className="h-display">Funnel</div>
          <div className="text-muted mt-2">
            Chi apre le email, chi apre il report, chi arriva alla catena e chi prenota la call
          </div>
        </div>
        <div className="flex gap-2 items-center" style={{flexWrap:"wrap"}}>
          {lists.length > 0 && (
            <select value={listId} onChange={(e)=>setListId(e.target.value)}
              data-testid="funnel-list-filter"
              style={{padding:"8px 10px",borderRadius:8,border:"1px solid #ddd",minWidth:190}}>
              <option value="">Tutte le liste</option>
              {lists.map(l => <option key={l.listId} value={l.listId}>{l.listName}</option>)}
            </select>
          )}
          {[7, 30, 90].map(d => (
            <button key={d} onClick={()=>setDays(d)} data-testid={`funnel-window-${d}`}
              className={`btn btn-sm ${days === d ? "btn-primary" : "btn-secondary"}`}>{d} giorni</button>
          ))}
        </div>
      </div>

      <div className="card mb-6" style={{padding:18}} data-testid="funnel-summary">
        {!summary ? <span className="spinner"/> : summary.stages[0].count === 0 ? (
          <div className="text-muted" data-testid="funnel-summary-empty">
            Nessuna email inviata negli ultimi {days} giorni.
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4" style={{flexWrap:"wrap",gap:8}}>
              <div className="h-section">Imbuto completo</div>
              <div className="text-small text-muted">
                {summary.analysed_leads} lead · tempo medio sul report {summary.avg_dwell_seconds}s
              </div>
            </div>
            <FunnelBars stages={summary.stages}/>
          </>
        )}
      </div>

      <div className="flex gap-2 mb-4" style={{flexWrap:"wrap"}} data-testid="funnel-filters">
        {FILTERS.map(f => (
          <button key={f.key} onClick={()=>setOnly(f.key)} data-testid={`funnel-filter-${f.key}`}
            className={`btn btn-sm ${only === f.key ? "btn-primary" : "btn-secondary"}`}>
            {f.key === "hot" && <Flame size={13}/>} {f.label}
            {f.key === "hot" && summary?.hot_leads ? ` (${summary.hot_leads})` : ""}
          </button>
        ))}
      </div>

      <div className="card" style={{padding:0}} data-testid="funnel-table">
        {leads === null ? (
          <div style={{padding:30}}><span className="spinner"/></div>
        ) : leads.length === 0 ? (
          <div className="text-muted text-center" style={{padding:40}} data-testid="funnel-table-empty">
            Nessun lead in questa vista.
          </div>
        ) : (
          <div style={{overflowX:"auto"}}>
            <table className="w-full" style={{borderCollapse:"collapse"}}>
              <thead>
                <tr style={{background:"#fafafa",fontSize:12,textTransform:"uppercase",
                     color:"var(--mv-text-muted)"}}>
                  <th style={{textAlign:"left",padding:"10px 14px"}}>Azienda</th>
                  <th style={{textAlign:"left",padding:"10px 8px"}}>Lista</th>
                  <th style={{textAlign:"left",padding:"10px 8px"}}>Fino a dove è arrivato</th>
                  <th style={{textAlign:"center",padding:"10px 8px"}}>Email</th>
                  <th style={{textAlign:"center",padding:"10px 8px"}}>Report</th>
                  <th style={{textAlign:"center",padding:"10px 8px"}}>Catena</th>
                  <th style={{textAlign:"center",padding:"10px 8px"}}>CTA</th>
                  <th style={{textAlign:"center",padding:"10px 8px"}}>Call</th>
                  <th style={{textAlign:"right",padding:"10px 8px"}}>Sul report</th>
                  <th style={{textAlign:"right",padding:"10px 14px"}}>Ultima attività</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(l => {
                  const c = STAGE_COLOR(l.stageIndex);
                  return (
                    <tr key={l.analysisId} onClick={()=>setOpen(l)}
                      data-testid={`funnel-row-${l.analysisId}`}
                      style={{borderTop:"1px solid #f0f0f0",cursor:"pointer"}}>
                      <td style={{padding:"12px 14px"}}>
                        <div style={{fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                          {l.companyName}
                          {l.hot && <Flame size={13} color="#ea580c" title="Lead caldo"/>}
                        </div>
                        <div className="text-small text-muted">{l.website || l.email || "—"}</div>
                      </td>
                      <td style={{padding:"12px 8px"}} className="text-small">{l.listName}</td>
                      <td style={{padding:"12px 8px"}}>
                        <span style={{background:c.bg,color:c.color,padding:"3px 10px",borderRadius:99,
                             fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>{l.stageLabel}</span>
                      </td>
                      <td style={{padding:"12px 8px",textAlign:"center"}}>
                        <Dot on={l.flags.email_aperta} title={`Email aperta: ${dt(l.emailOpenedAt)}`}/>
                      </td>
                      <td style={{padding:"12px 8px",textAlign:"center"}}>
                        <Dot on={l.flags.report_aperto} title={`Report aperto: ${dt(l.reportOpenedAt)}`}/>
                      </td>
                      <td style={{padding:"12px 8px",textAlign:"center"}}>
                        <Dot on={l.flags.catena_vista} title={`Catena vista: ${dt(l.chainSeenAt)}`}/>
                      </td>
                      <td style={{padding:"12px 8px",textAlign:"center"}}>
                        <Dot on={l.flags.cta_cliccato} title={`CTA cliccato: ${dt(l.ctaClickedAt)}`}/>
                      </td>
                      <td style={{padding:"12px 8px",textAlign:"center"}}>
                        {l.callBookedAt
                          ? <CalendarCheck size={15} color="var(--mv-success)" title={dt(l.callBookedAt)}/>
                          : <Dot on={false} title="Nessuna call"/>}
                      </td>
                      <td style={{padding:"12px 8px",textAlign:"right"}} className="text-small">
                        {l.dwellSeconds ? `${l.dwellSeconds}s` : "—"}
                      </td>
                      <td style={{padding:"12px 14px",textAlign:"right"}} className="text-small text-muted">
                        {dt(l.lastEventAt || l.sentAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && <Timeline lead={open} onClose={()=>setOpen(null)} onBooked={markBooked}/>}
    </div>
  );
}
